/**
 * Adapt a live `LobbyGridSummary` (from `useGrids()`) into the
 * lobby's `MockBoard` shape.
 *
 * The lobby's renderers (cards, canvas, theming, panels) all consume
 * `MockBoard` today — wrapping live grids in the same shape lets us
 * swap mocks for live data with zero churn in the downstream UI. As
 * Phase 2 lands per-surface live wiring (seats, identity, activity,
 * adapters), the visual fields below (skybox, hue, plinth) will drift
 * from the onchain data and gain creator-chosen overrides via the
 * launch wizard / curation API. Until then they're seeded from the
 * grid id so the same grid always renders the same scene.
 */
import type { LobbyGridSummary } from '@takeover/sdk';
import { formatMarketUsd } from '@/lib/utils';
import { MOCK_ETH_USD, MOCK_TAKEOVER_USD } from '@/features/lobby/data/mock-prices';
import { TAKEOVER_TOKEN_ADDRESS, isV22Grid, isV23Grid } from '@/lib/constants/addresses';
import { materializedRewardsRawOrZeroIfStale } from '@/lib/reward-rollup-display';
import { THEMING_NONE_SENTINEL } from '@/lib/theming-validation';
import { taxPayingSeatCount } from '@/lib/grid-display';
import {
  FLOOR_PENDING_USD,
  type MockBoard,
  type MockAdapter,
  type MockSkybox,
  type MockTaxToken,
  type MockBoardPlinth,
} from './mock-boards';

/**
 * Live token-price snapshot threaded into the converter so non-USDC
 * yield amounts render at real USD rates rather than the stale
 * `MOCK_TAKEOVER_USD` / `MOCK_ETH_USD` constants.
 *
 * Both fields are optional — the converter falls back to the mock
 * constants when a value is missing/zero. This keeps SSR and any
 * non-React caller working without forcing them to wire prices in,
 * but production callers (`useLobbyBoards`) MUST supply both so end
 * users see the right $-figures on every rail card.
 */
export interface LobbyTokenPricesUsd {
  takeoverUsd?: number;
  ethUsd?: number;
}

const KNOWN_TOKEN_SYMBOLS: Record<string, string> = {
  '0x0000000000000000000000000000000000000000': 'ETH',
  '0x4200000000000000000000000000000000000006': 'WETH',
  '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913': 'USDC',
  '0x036cbd53842c5426634e7929541ec2318f3dcf7e': 'USDC',
  '0x940181a94a35a4569e4529a3cdfb74e38fd98631': 'AERO',
};

function tokenSymbol(addr: string | undefined | null, fallback = 'ETH'): string {
  if (!addr) return fallback;
  const lower = addr.toLowerCase();
  // The TAKEOVER token address is env-driven (mainnet vs sepolia) so it
  // doesn't fit the static map above.
  if (TAKEOVER_TOKEN_ADDRESS && lower === TAKEOVER_TOKEN_ADDRESS) return 'TAKEOVER';
  return KNOWN_TOKEN_SYMBOLS[lower] ?? fallback;
}

const TAX_TOKEN_BY_SYMBOL: Record<string, MockTaxToken> = {
  USDC: 'USDC',
  WETH: 'WETH',
  ETH: 'WETH',
  TAKEOVER: 'TAKEOVER',
};

function taxTokenLabel(addr: string | undefined | null): MockTaxToken {
  const sym = tokenSymbol(addr, 'USDC');
  return TAX_TOKEN_BY_SYMBOL[sym] ?? 'USDC';
}

const ADAPTER_MAP: Record<string, MockAdapter> = {
  flaunch: 'flaunch',
  'uniswap-v3': 'uniswap-lp',
  'uniswap-v4': 'uniswap-lp',
  aerodrome: 'aerodrome',
  custom: 'custom',
};

function pickAdapter(g: LobbyGridSummary): MockAdapter {
  if (g.adapterType && ADAPTER_MAP[g.adapterType]) return ADAPTER_MAP[g.adapterType];
  const primary = g.assets?.find((a) => a.active) ?? g.assets?.[0];
  switch (primary?.assetType) {
    case 'flaunch':
      return 'flaunch';
    case 'lp':
      return 'uniswap-lp';
    case 'lp-v4':
      return 'uniswap-lp';
    case 'aerodrome':
      return 'aerodrome';
    default:
      return 'custom';
  }
}

const SKYBOX_BY_ADAPTER: Record<MockAdapter, MockSkybox> = {
  flaunch: 'aurora',
  'uniswap-lp': 'sci-fi',
  aerodrome: 'sky',
  'yield-vault': 'aurora',
  megapot: 'medieval',
  custom: 'farm',
};

const PLINTH_BY_SKYBOX: Partial<Record<MockSkybox, MockBoardPlinth>> = {
  aurora: 'obsidian',
  'sci-fi': 'slate',
  sky: 'marble',
  medieval: 'stone',
  farm: 'wood',
};

/** FNV-1a — kept consistent with the other lobby mock generators. */
function fnv1a(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h = Math.imul(h ^ input.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/**
 * Pick a hex hue from a hash so the same grid always tints the same.
 * Hex (not HSL) is required because `BoardCover` concatenates an alpha
 * suffix (`${hue}66`) for the title's text-shadow.
 */
function hueFromId(id: string): string {
  const h = fnv1a(id);
  const hueDeg = h % 360;
  return hslToHex(hueDeg, 70, 55);
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => {
    const v = lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(v * 255);
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

function ageDays(createdAt: number): number {
  const now = Math.floor(Date.now() / 1000);
  const sec = Math.max(0, now - createdAt);
  return Math.floor(sec / 86_400);
}

function bigIntStringOrZero(s: string | undefined | null): bigint {
  if (!s) return 0n;
  try {
    return BigInt(s);
  } catch {
    return 0n;
  }
}

/**
 * USD formatter for a USDC-denominated raw bigint string. Used for
 * `totalTvlUsd` (which is always USD-scaled).
 */
function formatUsdcBigint(s: string | undefined | null): string {
  const v = bigIntStringOrZero(s);
  if (v === 0n) return '$0';
  return formatMarketUsd(Number(v) / 1e6);
}

/**
 * Format a raw onchain price in the lobby's compact "floor" style.
 *
 * Render contract: every convertible tax token resolves to USD so the
 * lobby never displays mixed units side-by-side on the rail.
 *
 *   - USDC                → `$N` directly (1 token = $1).
 *   - TAKEOVER / WETH/ETH → multiply by the live oracle USD price from
 *                           `prices`. If that price hasn't resolved yet
 *                           (`undefined` / `0`), return `FLOOR_PENDING_USD`
 *                           so consumers render a skeleton instead of
 *                           leaking the raw token amount (e.g. the
 *                           `$24801.3k TAKEOVER` regression that
 *                           appeared after live boardroom data started
 *                           overlaying the static `$148` mock).
 *   - Other ERC20s        → no oracle wired (e.g. AERO); fall back to
 *                           the compact `"N SYMBOL"` token label so the
 *                           number is still legible. There's no USD
 *                           computation in flight to wait on, so a
 *                           skeleton would never resolve.
 */
function formatPriceBigint(
  raw: string | undefined | null,
  decimals: number,
  symbol: string,
  prices?: LobbyTokenPricesUsd,
): string {
  const v = bigIntStringOrZero(raw);
  if (v === 0n) return '$0';
  const num = Number(v) / 10 ** Math.max(0, decimals);
  if (symbol === 'USDC') return formatMarketUsd(num);
  if (symbol === 'TAKEOVER') {
    const px = prices?.takeoverUsd ?? 0;
    if (px <= 0) return FLOOR_PENDING_USD;
    return formatMarketUsd(num * px);
  }
  if (symbol === 'WETH' || symbol === 'ETH') {
    const px = prices?.ethUsd ?? 0;
    if (px <= 0) return FLOOR_PENDING_USD;
    return formatMarketUsd(num * px);
  }
  // Compact non-USD formatter for tokens with no live USD oracle —
  // mirrors the rewards/tvl style so the rail card never overflows.
  const compact = num >= 1_000
    ? `${(num / 1_000).toFixed(1)}k`
    : num >= 1
      ? num.toFixed(2)
      : num >= 0.01
        ? num.toFixed(3)
        : '<0.01';
  return `${compact} ${symbol}`;
}

/** Rough USD value for 7d yield so board cards always show `$…` like `$23.23K`.
 *
 *  The indexer's `yield7d` is a rolling 7d sum that only advances when
 *  a new distribution is indexed — long-idle pools therefore show the
 *  last non-decayed sum until housekeeping prunes it, which a viewer
 *  reads as "lifetime"-ish. `materializedRewardsRawOrZeroIfStale`
 *  zeroes the value once `yield7dUpdatedAt` is older than the
 *  rollup-stale window, matching the same staleness contract the
 *  graduation/leaderboard surfaces apply (`use-graduation-pools`).
 *  All V2 grids run on the same rollup pipeline so we always treat
 *  these summaries as `isV2Grid: true` for the staleness lookup.
 *
 *  Pricing: TAKEOVER and ETH amounts are converted to USD using the
 *  live prices from `prices` (provided by `useLobbyBoards` via the
 *  `useTakeoverPrice()` / `useEthPrice()` hooks). Missing or zero
 *  values fall back to the stale `MOCK_*` constants so static-context
 *  callers (SSR, tests) still produce a finite number — production UI
 *  paths always pass real prices. */
/**
 * Convert a raw yield-token amount to a USD number using live prices — with
 * NO staleness gating. Shared by `yield7dUsdNumber` (which feeds it the
 * staleness-gated rollup) and the Boardroom's live `GridRewardEvent` 7d-sum
 * override in `useLobbyBoards` (which already reflects real on-chain events,
 * so it must not be re-gated/zeroed).
 */
export function rawYieldToUsd(
  raw: string | null | undefined,
  yieldToken: string | null | undefined,
  yieldTokenDecimals: number | null | undefined,
  prices?: LobbyTokenPricesUsd,
): number {
  const v = bigIntStringOrZero(raw);
  if (v === 0n) return 0;
  const decimals = yieldTokenDecimals ?? 18;
  const num = Number(v) / 10 ** Math.max(0, decimals);
  const sym = tokenSymbol(yieldToken);
  if (sym === 'USDC') return num;
  const takeoverUsd = prices?.takeoverUsd && prices.takeoverUsd > 0 ? prices.takeoverUsd : MOCK_TAKEOVER_USD;
  const ethUsd = prices?.ethUsd && prices.ethUsd > 0 ? prices.ethUsd : MOCK_ETH_USD;
  if (sym === 'TAKEOVER') return num * takeoverUsd;
  if (sym === 'WETH' || sym === 'ETH') return num * ethUsd;
  return num;
}

function yield7dUsdNumber(g: LobbyGridSummary, prices?: LobbyTokenPricesUsd): number {
  const fresh = materializedRewardsRawOrZeroIfStale(
    g.yield7d,
    {
      isV2Grid: true,
      yield7dUpdatedAt: g.yield7dUpdatedAt ?? null,
      lastRewardAt: null,
    },
  );
  return rawYieldToUsd(fresh, g.yieldToken, g.yieldTokenDecimals, prices);
}

/**
 * 7-day rewards as a compact USD string (`formatMarketUsd`) for rail cards + tooltips.
 */
function formatYield7d(g: LobbyGridSummary, prices?: LobbyTokenPricesUsd): string {
  const usd = yield7dUsdNumber(g, prices);
  if (usd <= 0) return '$0';
  return formatMarketUsd(usd);
}

/**
 * Compact USD card string for an already-summed raw yield amount (no
 * staleness gating). Used for the Boardroom's live `GridRewardEvent` 7d
 * sum, which is more complete than the grid-level `yield7d` rollup — the
 * keeper-fee / Land-Protection reward rows flush into the boardroom and
 * are counted in `GridRewardEvent` but not in that rollup.
 */
export function formatYield7dFromRaw(
  raw: string | null | undefined,
  yieldToken: string | null | undefined,
  yieldTokenDecimals: number | null | undefined,
  prices?: LobbyTokenPricesUsd,
): string {
  const usd = rawYieldToUsd(raw, yieldToken, yieldTokenDecimals, prices);
  if (usd <= 0) return '$0';
  return formatMarketUsd(usd);
}

/**
 * Cumulative Harberger tax collected by a board, expressed as a USD
 * number. Drives the lobby directory's default sort key
 * (`MockBoard.taxesUsd`) and the `>$0.00 paid in tax` filter.
 *
 * The indexer exposes a lifetime `totalTaxCollected` per grid (raw
 * token units) but no rolling 7-day window the way `yield7d` does
 * for rewards. We therefore use the lifetime total here — a board
 * with active holders accumulates tax monotonically, so cumulative
 * collected is a reasonable proxy for "this board is generating
 * activity" until the indexer rolls up a `taxes7d` companion. Once
 * that field lands, swap in the windowed value here and the rest of
 * the lobby picks it up via the `taxesUsd` field on `MockBoard`.
 *
 * Pricing mirrors `yield7dUsdNumber` — USDC is 1:1, TAKEOVER /
 * (W)ETH convert via the live oracle prices on `prices`, anything
 * else falls through to the raw token amount in USD-equivalent
 * units (an underestimate, but never zero) so the value is always
 * a finite number for the sort comparator.
 */
function totalTaxesUsdNumber(
  g: LobbyGridSummary,
  prices?: LobbyTokenPricesUsd,
): number {
  const v = bigIntStringOrZero(g.totalTaxCollected);
  if (v === 0n) return 0;
  const decimals = g.taxTokenDecimals ?? 18;
  const num = Number(v) / 10 ** Math.max(0, decimals);
  const sym = taxTokenLabel(g.taxToken);
  if (sym === 'USDC') return num;
  const takeoverUsd =
    prices?.takeoverUsd && prices.takeoverUsd > 0
      ? prices.takeoverUsd
      : MOCK_TAKEOVER_USD;
  const ethUsd =
    prices?.ethUsd && prices.ethUsd > 0 ? prices.ethUsd : MOCK_ETH_USD;
  if (sym === 'TAKEOVER') return num * takeoverUsd;
  if (sym === 'WETH') return num * ethUsd;
  return num;
}

/**
 * Cumulative tax collected as a compact USD string. Uses
 * `totalTaxesUsdNumber` so the formatted value matches the sort
 * comparator's input — boards with `> $0.00` paid in tax always
 * pass the lobby's "show me boards with activity" filter, and the
 * sort comparator's `parseNum(taxesUsd)` reads back the same number.
 */
function formatTaxesUsd(
  g: LobbyGridSummary,
  prices?: LobbyTokenPricesUsd,
): string {
  const usd = totalTaxesUsdNumber(g, prices);
  if (usd <= 0) return '$0';
  return formatMarketUsd(usd);
}

/**
 * Convert an onchain `totalSeats` count to its NxN side length.
 *
 * Exported so callers that have the live `totalSeats` on hand (e.g.
 * `LobbyClient.tsx`'s `activeBoardGridSize` cold-load override, when
 * `gridPage.grid.totalSeats` lands before `useGrids()` resolves) can
 * derive the same side length the lobby card / canvas / plinth use,
 * instead of falling back to the placeholder default (10) and briefly
 * painting the wrong grid dimension.
 *
 * `Math.round` (rather than `Math.ceil`) is the right rounding here:
 * every supported board size in `[takeover-app/src/features/lobby/launch/data/constants.ts](takeover-app/src/features/lobby/launch/data/constants.ts)`
 * is a perfect square (4, 9, 16, 25, 36, 64, 100, 144, 196, 256, …),
 * so `round(sqrt(n))` is exact and stable across float drift.
 */
export function gridSizeFromTotalSeats(total: number): number {
  if (total <= 0) return 10;
  const size = Math.round(Math.sqrt(total));
  return size > 0 ? size : 10;
}

function slugFromName(name: string, fallback: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

/**
 * Tagline copy for a `MockBoard` derived from a live grid. Uses the
 * shared `taxPayingSeatCount` helper from `@/lib/grid-display` so the
 * lobby card, the admin Boards tab, and any future seat-fraction
 * surface stay in lockstep on reorg-clamp behaviour.
 *
 * Historical note: this used to subtract `seatsSoldFromCreator` from
 * `activeSeatCount` on the theory that primary-mint creator seats are
 * tax-exempt. That arithmetic was systematically wrong on boards
 * where the creator was never the initial holder (the Boardroom, where
 * every seat was claimed directly by a player): it read `77 / 256`
 * on a board where all 256 seats were paying tax, because
 * `seatsSoldFromCreator` under-counted by the creator-less claim path.
 * `taxPayingSeatCount` uses `activeSeatCount` directly — every held
 * seat is taxable — and clamps against `totalSeats` for the indexer
 * reorg window.
 */
function taglineFromGrid(g: LobbyGridSummary): string {
  return `${taxPayingSeatCount(g)} / ${g.totalSeats} seats`;
}

/**
 * Tri-state skybox/plinth resolution shared between the lobby-list
 * mapper and the per-grid `LobbyClient.activeBoard` overlay.
 *
 *   - `'none'` sentinel → explicit None; renderers skip the layer
 *     (`null` reaches the canvas).
 *   - registered theme id → use it.
 *   - any other string / `undefined` → "no choice yet"; consumers may
 *     apply their own default (e.g. adapter-derived skybox in the
 *     lobby list, or `undefined` when the consumer wants to leave the
 *     existing value untouched in a partial overlay).
 */
export function coerceBoardSkybox(
  raw: string | null | undefined,
): MockSkybox | null | undefined {
  if (raw === THEMING_NONE_SENTINEL) return null;
  if (typeof raw !== 'string' || raw === '') return undefined;
  return raw as MockSkybox;
}

export function coerceBoardPlinth(
  raw: string | null | undefined,
): MockBoardPlinth | null | undefined {
  if (raw === THEMING_NONE_SENTINEL) return null;
  if (typeof raw !== 'string' || raw === '') return undefined;
  return raw as MockBoardPlinth;
}

/**
 * Convert a single live grid summary into a `MockBoard`-shaped row.
 * Visual fields are seeded from the grid id so the same grid renders
 * the same scene across requests/sessions. Identity, socials and
 * descriptions stay empty until the launch wizard surfaces them.
 *
 * `prices` is threaded through to `formatYield7d` so non-USDC yields
 * render at live USD rates. See the `LobbyTokenPricesUsd` doc and the
 * `useLobbyBoards` consumer for the production wiring.
 */
/**
 * Resolve a board's skybox + plinth exactly the way the live lobby /
 * active board does, so any surface (lobby card, board scene, replay)
 * paints the same theme.
 *
 * Three-state contract for each:
 *   - `'none'` sentinel  → creator explicitly picked None → `null`
 *                          (render no skybox / no slab).
 *   - registered theme   → use the chosen theme verbatim.
 *   - missing / unknown  → legacy board with no metadata → adapter-derived
 *                          skybox default, and a skybox-derived plinth.
 *
 * Accepts the minimal slice of a {@link LobbyGridSummary} the resolution
 * actually reads, so non-lobby callers (e.g. the replay board-meta) can
 * pass a lighter object.
 */
export function resolveBoardScene(g: {
  boardSkybox?: string | null;
  boardPlinth?: string | null;
  adapterType?: LobbyGridSummary['adapterType'];
  assets?: LobbyGridSummary['assets'];
}): { skybox: MockSkybox | null; boardPlinth: MockBoardPlinth | null | undefined } {
  const adapter = pickAdapter(g as LobbyGridSummary);

  const coercedSkybox = coerceBoardSkybox(g.boardSkybox);
  const skybox: MockSkybox | null =
    coercedSkybox === undefined
      ? (SKYBOX_BY_ADAPTER[adapter] ?? 'farm')
      : coercedSkybox;

  const coercedPlinth = coerceBoardPlinth(g.boardPlinth);
  const boardPlinth: MockBoardPlinth | null | undefined =
    coercedPlinth === undefined
      ? skybox
        ? PLINTH_BY_SKYBOX[skybox]
        : undefined
      : coercedPlinth;

  return { skybox, boardPlinth };
}

export function gridSummaryToMockBoard(
  g: LobbyGridSummary,
  prices?: LobbyTokenPricesUsd,
): MockBoard {
  const adapter = pickAdapter(g);
  const { skybox, boardPlinth } = resolveBoardScene(g);

  const yieldSym = tokenSymbol(g.yieldToken);
  const taxSym = taxTokenLabel(g.taxToken);
  const display = g.name ?? `Grid ${g.id.slice(-6)}`;

  return {
    id: g.id,
    slug: slugFromName(display, g.id),
    name: display,
    tagline: taglineFromGrid(g),
    // Prefer the live `pool_metadata.description` so the lobby's hover
    // tooltip stays in lock-step with the board page's own info card —
    // editing the board metadata used to leave every lobby card pinned
    // to the canonical fallback below until a full reload (and never
    // surface at all on the pinned Boardroom card, since
    // `brandBoardroomOverlay` froze the description). Empty strings
    // fall through to the same fallback so a creator who clears the
    // field doesn't end up with a blank tooltip.
    description:
      g.description && g.description.trim().length > 0
        ? g.description
        : "Land holders get access to rewards from the board's Yield and private group chat.",
    socials: {},
    ...(g.imageUrl ? { imageUrl: g.imageUrl } : {}),
    gridSize: gridSizeFromTotalSeats(g.totalSeats),
    capacity: g.totalSeats,
    taken: taxPayingSeatCount(g),
    taxToken: taxSym,
    floor: formatPriceBigint(g.floorPrice, g.taxTokenDecimals ?? 18, taxSym, prices),
    rewards7d: formatYield7d(g, prices),
    taxesUsd: formatTaxesUsd(g, prices),
    // Lifetime rewards in USD = cumulative yield received + cumulative
    // tax collected. Tax alone (`taxesUsd`) badly undercounts
    // yield-heavy boards (e.g. the Boardroom), so the landing's "Total
    // rewards" aggregate sums this instead. Numeric (not formatted) so
    // callers can total it without re-parsing.
    rewardsTotalUsd:
      rawYieldToUsd(
        g.totalYieldReceived,
        g.yieldToken,
        g.yieldTokenDecimals,
        prices,
      ) + totalTaxesUsdNumber(g, prices),
    tvl: formatUsdcBigint(g.totalTvlUsd),
    chatOnline: null,
    adapter,
    yieldToken: yieldSym,
    skybox,
    // `null` = explicit None (creator picked it), surfaced as a real
    // property so renderers can distinguish it from "no choice yet"
    // (the absent property, which falls back to the skybox default).
    ...(boardPlinth === undefined ? {} : { boardPlinth }),
    hue: hueFromId(g.id),
    pinned: false,
    premium: Boolean(g.curated),
    ageDays: ageDays(g.createdAt),
    // Pass the sub-day timestamp through verbatim — the lobby's "less
    // than 4h old" promotion (curated rail + NEW pill, see
    // `isBoardNew`) needs second-level precision that `ageDays` lost
    // by flooring to whole days.
    createdAtSec: g.createdAt,
    // Forward the per-week Harberger tax (in bps) so the metadata
    // panel can show e.g. "10%/wk tax". Boards launched before the
    // wizard exposed this field still index with 500 (legacy 5%).
    taxRateBps: g.taxRateBps,
    // Forward the UGM contract version so the metadata panel can
    // render the right forfeit-mode chip (Dutch auction vs instant
    // free-claim). Derived from `g.managerAddress` via the env-driven
    // `isV23Grid` / `isV22Grid` allowlists — the SDK's
    // `LobbyGridSummary` doesn't expose a `gridVersion` field
    // directly, but the manager address is the canonical identity
    // (see `lib/constants/addresses.ts`). Boardroom + every legacy
    // grid collapses to `'v2.1'`. V2.3 is treated as a peer of V2.2
    // for chip-rendering purposes (same Dutch-auction forfeit mode);
    // chip code only branches on the dutch-vs-instant distinction.
    gridVersion: isV23Grid(g.managerAddress)
      ? 'v2.3'
      : isV22Grid(g.managerAddress)
        ? 'v2.2'
        : 'v2.1',
    // Activity-sort inputs: holders + last-buy recency + chat traffic.
    // Fields are nullable / optional on `LobbyGridSummary` (older
    // gateway payloads, SSR with the boardroom env unconfigured),
    // so default to a "deactivated" state (`0` / `null`) rather
    // than throwing — `activityScore` reads those as the floor.
    uniqueHolderCount: g.uniqueHolderCount ?? 0,
    lastSeatPurchasedAtSec: g.lastSeatPurchasedAt ?? null,
    chatMessages7d: g.chatMessages7d ?? 0,
    // Lowercased so the BoardDirectory `myBoards` filter can compare
    // against the viewer's lowercased wagmi address without re-casing
    // on every render. The SDK already normalises `g.creator` to a
    // lowercase address, but we coerce defensively in case the cache
    // shape ever shifts.
    ...(g.creator ? { creatorAddress: g.creator.toLowerCase() } : {}),
    topHolders: [],
  };
}
